/**
 * Sistema de Telemetria para Rastreamento de Erros 429
 * Registra detalhes sobre erros de rate limiting para análise
 */

class Telemetry {
  constructor() {
    this.logs = [];
    this.maxLogs = 100; // Manter últimos 100 registros
    this.flushInterval = 5 * 60 * 1000; // Enviar a cada 5 minutos
    this.setupAutoFlush();
  }

  // Registrar evento de erro 429
  log429Error(data) {
    const log = {
      type: "429_RATE_LIMIT",
      timestamp: new Date().toISOString(),
      endpoint: data.endpoint,
      attempt: data.attempt,
      retryDelay: data.retryDelay,
      retryAfter: data.retryAfter,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "N/A",
      url: typeof window !== "undefined" ? window.location.href : "N/A",
      ...data,
    };

    this.logs.push(log);

    // Manter apenas os últimos X registros
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Log em desenvolvimento para análise
    if (process.env.NODE_ENV === "development") {
      console.warn("[TELEMETRY] 429 Error recorded:", log);
    }

    return log;
  }

  // Registrar retry de requisição
  logRetry(data) {
    const log = {
      type: "REQUEST_RETRY",
      timestamp: new Date().toISOString(),
      endpoint: data.endpoint,
      statusCode: data.statusCode,
      attemptNumber: data.attemptNumber,
      maxAttempts: data.maxAttempts,
      delayMs: data.delayMs,
      reason: data.reason,
      ...data,
    };

    this.logs.push(log);

    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    return log;
  }

  // Registrar cache hit/miss
  logCacheEvent(data) {
    const log = {
      type: data.type === "hit" ? "CACHE_HIT" : "CACHE_MISS",
      timestamp: new Date().toISOString(),
      endpoint: data.endpoint,
      ttl: data.ttl,
      cachedAt: data.cachedAt,
      expiresAt: data.expiresAt,
      ...data,
    };

    this.logs.push(log);

    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    return log;
  }

  // Registrar deduplicação de requisição
  logDeduplication(data) {
    const log = {
      type: "REQUEST_DEDUPLICATION",
      timestamp: new Date().toISOString(),
      requestKey: data.requestKey,
      endpoint: data.endpoint,
      savedCalls: data.savedCalls || 1,
      ...data,
    };

    this.logs.push(log);

    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    return log;
  }

  // Obter resumo de erros 429
  get429Summary() {
    const errors429 = this.logs.filter((log) => log.type === "429_RATE_LIMIT");

    if (errors429.length === 0) {
      return null;
    }

    const endpointStats = {};
    errors429.forEach((error) => {
      if (!endpointStats[error.endpoint]) {
        endpointStats[error.endpoint] = {
          count: 0,
          totalRetryDelay: 0,
          attempts: [],
        };
      }
      endpointStats[error.endpoint].count++;
      endpointStats[error.endpoint].totalRetryDelay += error.retryDelay || 0;
      endpointStats[error.endpoint].attempts.push(error.attempt);
    });

    return {
      total429Errors: errors429.length,
      endpointStats,
      mostAffectedEndpoint: Object.entries(endpointStats).reduce((a, b) =>
        a[1].count > b[1].count ? a : b
      )[0],
      averageRetryDelay:
        errors429.reduce((sum, e) => sum + (e.retryDelay || 0), 0) /
        errors429.length,
    };
  }

  // Obter resumo de cache
  getCacheSummary() {
    const cacheEvents = this.logs.filter((log) => log.type.includes("CACHE_"));

    if (cacheEvents.length === 0) {
      return null;
    }

    const hits = cacheEvents.filter((e) => e.type === "CACHE_HIT").length;
    const misses = cacheEvents.filter((e) => e.type === "CACHE_MISS").length;

    return {
      cacheHits: hits,
      cacheMisses: misses,
      hitRate: misses + hits > 0 ? (hits / (hits + misses)) * 100 : 0,
      totalCacheEvents: cacheEvents.length,
    };
  }

  // Obter resumo de deduplicação
  getDeduplicationSummary() {
    const dedup = this.logs.filter(
      (log) => log.type === "REQUEST_DEDUPLICATION"
    );

    if (dedup.length === 0) {
      return null;
    }

    return {
      totalDeduplicatedRequests: dedup.length,
      totalCallsSaved: dedup.reduce((sum, d) => sum + (d.savedCalls || 1), 0),
      endpointBreakdown: Object.fromEntries(
        Object.entries(
          dedup.reduce((acc, d) => {
            acc[d.endpoint] = (acc[d.endpoint] || 0) + (d.savedCalls || 1);
            return acc;
          }, {})
        )
      ),
    };
  }

  // Obter relatório completo
  getFullReport() {
    return {
      timestamp: new Date().toISOString(),
      totalLogs: this.logs.length,
      error429Summary: this.get429Summary(),
      cacheSummary: this.getCacheSummary(),
      deduplicationSummary: this.getDeduplicationSummary(),
      recentLogs: this.logs.slice(-10), // Últimos 10
    };
  }

  // Enviar telemetria para o servidor (estratégia 5)
  async flushToServer() {
    if (this.logs.length === 0) {
      return;
    }

    try {
      const report = this.getFullReport();

      // Enviar para o backend para análise
      // Nota: Implementar o endpoint /api/telemetry no backend
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:8080/api"
        }/telemetry`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            type: "API_QUEUE_TELEMETRY",
            data: report,
            timestamp: Date.now(),
          }),
        }
      );

      if (response.ok) {
        if (process.env.NODE_ENV === "development") {
          console.log("✅ Telemetria enviada para o servidor");
        }
        // Limpar logs após envio bem-sucedido
        this.logs = [];
      } else {
        console.warn(
          "⚠️ Erro ao enviar telemetria:",
          response.status,
          response.statusText
        );
      }
    } catch (error) {
      console.error(
        "❌ Erro ao enviar telemetria para o servidor:",
        error.message
      );
    }
  }

  // Configurar envio automático de telemetria
  setupAutoFlush() {
    setInterval(() => {
      this.flushToServer();
    }, this.flushInterval);
  }

  // Limpar todos os logs
  clear() {
    this.logs = [];
  }

  // Exportar logs em JSON
  exportJSON() {
    return JSON.stringify(this.getFullReport(), null, 2);
  }

  // Exportar logs em CSV (simplificado)
  exportCSV() {
    if (this.logs.length === 0) {
      return "";
    }

    const headers = Object.keys(this.logs[0]);
    const rows = this.logs.map((log) =>
      headers.map((h) => {
        const value = log[h];
        if (typeof value === "object") {
          return JSON.stringify(value);
        }
        return value;
      })
    );

    return (
      headers.join(",") + "\n" + rows.map((row) => row.join(",")).join("\n")
    );
  }

  // Descarregar relatório
  downloadReport(format = "json") {
    const data = format === "json" ? this.exportJSON() : this.exportCSV();
    const filename = `api-telemetry-${Date.now()}.${format}`;
    const element = document.createElement("a");
    element.setAttribute(
      "href",
      `data:text/${
        format === "json" ? "json" : "csv"
      };charset=utf-8,${encodeURIComponent(data)}`
    );
    element.setAttribute("download", filename);
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  }
}

// Instância global de telemetria
const telemetry = new Telemetry();

export default telemetry;
